<template>
  <v-container fluid class="pa-6 pa-md-8">
    <div class="page-head d-flex flex-wrap align-center justify-space-between">
      <div>
        <h1 class="page-head__title">
          {{
            $route.query.name
              ? `${$route.query.name} Transactions`
              : "School Transactions"
          }}
        </h1>
        <Breadcrumbs
          :items="[
            { text: 'All Schools', disabled: false, href: '/admin/schools/all' },
            { text: 'Transactions', disabled: true }
          ]"
        />
      </div>
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        :return-value.sync="dates"
        transition="scale-transition"
        offset-y
        min-width="auto"
        :nudge-left="120"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            outlined
            v-bind="attrs"
            v-on="on"
            class="toolbar-btn text-none"
            :class="{ 'toolbar-btn--active': dates.length > 0 }"
          >
            <v-icon small left>mdi-calendar-range-outline</v-icon>
            {{ dates.length > 0 ? dateRangeText : "Choose date range" }}
            <v-icon
              v-if="dates.length === 2"
              small
              right
              @click.stop="clearDates"
              >mdi-close</v-icon
            >
          </v-btn>
        </template>
        <v-date-picker v-model="dates" range color="primary">
          <v-spacer></v-spacer>
          <v-btn small text color="primary" @click="menu = false">
            Cancel
          </v-btn>
          <v-btn @click="setFilteredDate" text color="primary">OK</v-btn>
        </v-date-picker>
      </v-menu>
    </div>

    <div class="toolbar d-flex align-center mb-4">
      <v-text-field
        outlined
        dense
        rounded
        hide-details
        prepend-inner-icon="mdi-magnify"
        placeholder="Search by account number…"
        v-model="search"
        color="primary"
        class="toolbar__search"
      />
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            class="ml-2 toolbar__refresh"
            :loading="pageLoading"
            @click="fetchTransactions"
            v-on="on"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>Get latest transactions</span>
      </v-tooltip>
    </div>

    <v-data-table
      :headers="headers"
      :items="transactions"
      :loading="pageLoading"
      item-key="branch"
      :server-items-length="paginate.total"
      :options.sync="options"
      class="data-table"
      loading-text="Loading… Please wait"
      :footer-props="{ itemsPerPageOptions: [30, 40, 50] }"
    >
      <template #item.memberId="{ item }">
        <span v-if="item.memberId" class="cell-strong text-capitalize"
          >{{ item.memberId.firstName ? item.memberId.firstName : "" }}
          {{ item.memberId.lastName ? item.memberId.lastName : "" }}</span
        >
        <span v-else-if="item.studentId" class="cell-strong text-capitalize">{{
          item.studentId.name
        }}</span>
        <span v-else class="text-muted">N/A</span>
      </template>
      <template #item.totalAmount="{ item }">
        <span class="tabular cell-amount"
          >GHC {{ item.totalAmount ? item.totalAmount : "N/A" }}</span
        >
      </template>
      <template #item.service="{ item }">
        <span class="pill pill--brand text-uppercase">{{ item.service }}</span>
      </template>
      <template #item.paymentStatus="{ item }">
        <span
          v-if="
            item.paymentStatus &&
              item.paymentStatus.toLowerCase() === 'success'
          "
          class="pill pill--success text-uppercase"
          >{{ item.paymentStatus }}</span
        >
        <span v-else class="pill pill--pending text-uppercase">{{
          item.paymentStatus ? item.paymentStatus : "N/A"
        }}</span>
      </template>
      <template #item.createdAt="{ item }">
        <span class="text-muted">{{ item.createdAt | dateFormat }}</span>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { debounce } from "lodash";
import moment from "moment";
import { loadComponent } from "@/utils/helpers";
export default {
  name: "Transactions",

  data() {
    return {
      search: "",
      options: {
        page: 1,
        itemsPerPage: 30
      },
      menu: false,
      dates: [],
      chosenDateRange: []
    };
  },
  components: {
    Breadcrumbs: loadComponent(`component/Breadcrumbs`)
  },

  computed: {
    ...mapGetters({
      pageLoading: "getIsPageLoading",
      paginate: "transactions/getPagination",
      transactions: "transactions/getAllTransactions"
    }),
    dateRangeText() {
      return this.dates.join(" ~ ");
    },
    transactionsList() {
      return this.transactions.filter(searchTerm => {
        return (
          searchTerm.sequenceID
            .toLowerCase()
            .indexOf(this.search.toLowerCase()) !== -1
        );
      });
    },

    headers() {
      return [
        {
          text: "Date",
          value: "createdAt",
          filterable: false,
          sortable: false
        },
        {
          text: "Account Number",
          value: "accountNumber",
          filterable: true,
          sortable: false
        },
        {
          text: "Member Name",
          value: "memberId",
          filterable: true,
          sortable: false
        },
        {
          text: "Total Amount (GHS)",
          value: "totalAmount",
          filterable: true,
          sortable: false
        },
        {
          text: "Service",
          value: "service",
          sortable: false
        },
        {
          text: "Payment Status",
          value: "paymentStatus",
          sortable: false
        }
      ];
    }
  },
  filters: {
    status(value) {
      return !value ? "Active" : "Inactive";
    },
    firstLetterFunction(value) {
      return value.charAt(0).toUpperCase();
    },
    dateFormat(value) {
      return moment(value).format("Do MMM,YYYY HH:mm:ss");
    }
  },

  watch: {
    search() {
      this.resetAndFetch();
    },
    chosenDateRange() {
      this.resetAndFetch();
    },
    options: {
      handler() {
        this.fetchTransactions();
      },
      deep: true
    }
  },

  methods: {
    // Single source of truth so search + date range + pagination compose into
    // one request (previously pagination rebuilt the query without the filters).
    buildQuery() {
      const { page, itemsPerPage } = this.options;
      const params = new URLSearchParams();
      params.set("page", page || 1);
      params.set("size", itemsPerPage || 30);
      params.set("schoolId", this.$route.params.id);
      if (this.search) params.set("searchTerm", this.search);
      if (this.chosenDateRange.length === 2) {
        const [startDate, endDate] = this.chosenDateRange;
        params.set("startDate", startDate);
        params.set("endDate", endDate);
      }
      return `?${params.toString()}`;
    },
    fetchTransactions: debounce(function() {
      const query = this.buildQuery();
      if (this.search) {
        this.$store.dispatch("transactions/searchList", query);
      } else {
        this.$store.dispatch("transactions/list", query);
      }
    }, 300),
    resetAndFetch() {
      this.options.page = 1;
      this.fetchTransactions();
    },
    setFilteredDate() {
      this.$refs.menu.save(this.dates);
      // v-date-picker returns the range in selection order — sort so start<=end.
      this.chosenDateRange = [...this.dates].sort();
    },
    clearDates() {
      this.dates = [];
      this.chosenDateRange = [];
    }
  },
  created() {
    this.fetchTransactions();
  }
};
</script>

<style scoped>
.toolbar__search {
  max-width: 440px;
  width: 100%;
}
.toolbar__refresh {
  color: var(--muted-foreground);
}
.toolbar-btn {
  border-color: var(--border) !important;
  color: var(--muted-foreground) !important;
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 500;
}
.toolbar-btn--active {
  border-color: var(--signal) !important;
  color: var(--signal) !important;
}
.cell-strong {
  font-weight: 600;
  color: var(--foreground);
}
.cell-amount {
  font-weight: 600;
  color: var(--foreground);
}
.text-muted {
  color: var(--muted-foreground);
}
</style>
